import { Flex, Text, useDisclosure } from "@chakra-ui/react";
import { BaseTable } from "@/components/BaseTable/BaseTable";
import { BaseTableFilters } from "@/components/BaseTableFilters/BaseTableFilters";
import { ColumnDef } from "@tanstack/react-table";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Layout } from "@/components/Layout/Layout";
import { RootState, useAppDispatch, useAppSelector } from "@/store";
import { useEffect, useMemo, useState, useCallback } from "react";
import { fetchAllApplications } from "@/store/slices/applications";
import { Pagination } from "@/components/Pagination/Pagination";
import { useSorting } from "@/hooks/useSorting";
import api from "@/api";
import { Recruiter } from "@/model/recruiters";
import { BaseSelect } from "@/components/Controls/BaseSelect";
import { Customer } from "@/model/customers";
import { AssignModal } from "@/components/AssignModal/AssignModal";
import { RecruitmentChannel } from "@/model/recruitment";
import { Department as DepartmentModel } from "@/model/department";
import { Project } from "@/model/projects";
import { ApplicationFilters } from "@/model/applications";
import { UserRole } from "@/api/auth/types";
import { selectUserRole } from "@/store/slices/user";
import { SortingTypes } from "@/helpers/SortingTypes";
import { FiltersPopover } from "./components/FiltersPopover";
import { TabsBar } from "./components/TabsBar";
import { useApplicationColumns } from "./hooks/use-application-columns";
import { toUnix } from "@/utils/date";
import { updateRouteQuery } from "@/utils/route";
import { useSessionStorageFilters } from "@/hooks/useSessionStorageFilters";
import { defaultApplicationFilterFields } from "./_helpers";

function getDefaultPaginatedListFilters(sort_column: string) {
  return {
    limit: "1000",
    offset: "0",
    sort_column: `${sort_column}.created_at`,
    sort_direction: SortingTypes.DESC,
  } as const;
}

export const ApplicationsPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { applications, isLoading } = useAppSelector((state: RootState) => state.applications);
  const { guides } = useAppSelector((state: RootState) => state.guides);

  const role = useAppSelector(selectUserRole);
  const isRecruiter = role === UserRole.RECRUITER;
  const isHRD = role === UserRole.HRD;
  const isHeadOfDepartment = role === UserRole.DEPARTMENT_MANAGER;
  const isFlatListMode = isRecruiter || isHRD || isHeadOfDepartment;

  const [recruiters, setRecruiters] = useState<Recruiter[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [allProjects, setAllProjects] = useState<Project[]>([]);
  const [channels, setChannels] = useState<(RecruitmentChannel & { departments?: DepartmentModel[] })[]>([]);

  const { sortColumn, sortDirection, handleSort } = useSorting("application.created_at");

  const { isOpen: isFiltersOpen, onOpen: onFiltersOpen, onClose: onFiltersClose } = useDisclosure();

  const [activeTab, setActiveTab] = useState<"unsorted" | "assigned">((searchParams.get("tab") as "unsorted" | "assigned") || "unsorted");

  const showStatusFilters = activeTab !== "unsorted" || isFlatListMode;
  const [isAssignOpen, setAssignOpen] = useState(false);
  const [selectedApplicationId, setSelectedApplicationId] = useState<number | null>(null);

  // Используем универсальный хук для работы с фильтрами
  const { formik, handleApplyFilters, handleResetFilters } = useSessionStorageFilters<ApplicationFilters>({
    storageKey: "applicationsPageFilters",
    defaultValues: defaultApplicationFilterFields,
  });

  const resetPagination = useCallback(() => {
    const limitValue = formik.values.limit || "20";
    const params = new URLSearchParams(searchParams);
    params.set("page", "1");
    params.set("limit", String(limitValue));
    navigate(`?${params.toString()}`, { replace: true });
    formik.setFieldValue("offset", "0");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, navigate]);

  const handleCreate = () => {
    navigate("/applications/create");
  };

  const handleSearch = (value: string) => {
    formik.setFieldValue("search", value);
    resetPagination();
  };

  const statusOptions = useMemo(
    () =>
      Object.entries(guides?.ApplicationStatus || {}).map(([key, val]) => ({
        value: key,
        label: String(val),
      })),
    [guides],
  );

  const filteredStatusOptions = useMemo(() => statusOptions.filter((opt) => opt.value !== "5"), [statusOptions]);

  const appliedFiltersCount = useMemo(() => {
    let count = 0;
    if (formik.values.customer_id) count += 1;
    if (formik.values.project_id) count += 1;
    if (formik.values.created_by) count += 1;
    if (formik.values.created_at_from) count += 1;
    if (formik.values.created_at_to) count += 1;
    const st = Array.isArray(formik.values["status[]"]) && formik.values["status[]"]!.length > 0 ? formik.values["status[]"]![0] : "";
    if (showStatusFilters && st && st !== "5") count += 1;
    return count;
  }, [formik.values, showStatusFilters]);

  const refreshApplications = useCallback(() => {
    const rawForm = Object.fromEntries(Object.entries(formik.values).filter(([, v]) => v !== undefined && v !== "")) as ApplicationFilters;

    const { created_at_from, created_at_to, ...rest } = rawForm;
    const filters: ApplicationFilters = {
      ...rest,
      created_at_from: toUnix(created_at_from),
      created_at_to: toUnix(created_at_to),
    };

    if (filters?.["status[]"]?.length === 0 || filters?.["status[]"] === undefined) {
      filters["status[]"] = activeTab === "unsorted" ? ["5"] : ["1", "2", "3", "4"];
    }

    dispatch(fetchAllApplications({ params: { filters } }));
  }, [formik.values, activeTab, dispatch]);

  useEffect(() => {
    refreshApplications();
  }, [formik.values, activeTab, refreshApplications]);

  useEffect(() => {
    const values = formik.values;
    const statusValue = Array.isArray(values["status[]"]) && values["status[]"].length > 0 ? String(values["status[]"][0]) : "";
    updateRouteQuery(
      {
        tab: activeTab,
        search: values.search || "",
        channel_id: activeTab === "unsorted" ? "" : values.channel_id || "",
        customer_id: values.customer_id || "",
        project_id: values.project_id || "",
        status: activeTab === "unsorted" ? "" : statusValue,
        created_by: values.created_by || "",
        created_at_from: values.created_at_from || "",
        created_at_to: values.created_at_to || "",
        sort_column: values.sort_column || "",
        sort_direction: values.sort_direction || "",
        limit: values.limit || "",
        offset: values.offset || "",
      },
      { replace: true },
    );
  }, [formik.values, activeTab]);

  useEffect(() => {
    try {
      (async () => {
        const [recruitersRes, channelsRes, customersRes, projectsRes] = await Promise.all([
          api.recruiters.recruiters({}),
          api.channel.getChannels({ main: true }),
          api.customers.customers({ params: { filters: getDefaultPaginatedListFilters("customer") } }),
          api.projects.projects({ params: { filters: getDefaultPaginatedListFilters("project"), expand: ["customer"] } }),
        ]);
        setRecruiters(recruitersRes?.data || []);
        setChannels(channelsRes?.data || []);
        setCustomers(customersRes?.data?.items || []);
        setAllProjects(projectsRes?.data?.items || []);
      })();
    } catch (error) {
      console.log("error", error);
    }
  }, []);

  const handleOpenAssign = (id: number) => {
    setSelectedApplicationId(id);
    setAssignOpen(true);
  };

  const columns = useApplicationColumns(applications.can_manage_channel, handleOpenAssign) as Array<ColumnDef<any, any>>;

  return (
    <Layout>
      <Flex flexDirection="column" maxH="90vh" overflowY="auto" gap="24px">
        <Text fontSize={27} fontWeight={500}>
          Заявки
        </Text>
        <BaseTableFilters
          handleSearch={handleSearch}
          placeHolderInput="Поиск по названию заявки"
          searchValue={formik.values.search}
          selectedFiltersCount={appliedFiltersCount}
          isShowCreateButton={applications.can_add}
          onClickCreate={handleCreate}
          isLoading={isLoading}
          isOpenPopupFilters={isFiltersOpen}
          onClosePopupFilters={onFiltersClose}
          onClickBtnPopupFilters={onFiltersOpen}
          popupFilters={
            <FiltersPopover
              recruiters={recruiters}
              customers={customers}
              allProjects={allProjects}
              filteredStatusOptions={filteredStatusOptions}
              showStatus={showStatusFilters}
              values={{
                customer_id: formik.values.customer_id || "",
                project_id: formik.values.project_id || "",
                ["status[]"]: formik.values["status[]"],
                created_by: formik.values.created_by || "",
                created_at_from: formik.values.created_at_from || "",
                created_at_to: formik.values.created_at_to || "",
              }}
              onApply={(submitted) => {
                handleApplyFilters(submitted);
                onFiltersClose();
              }}
              onReset={() => {
                handleResetFilters();
              }}
            />
          }
        >
          {!isFlatListMode && activeTab !== "unsorted" && (
            <Flex gap="8px" alignItems="center">
              <BaseSelect
                options={channels.map((c) => ({ value: String(c.id), label: c.name }))}
                placeholder="Выберите канал"
                value={formik.values.channel_id || null}
                onChange={(val) => {
                  formik.setFieldValue("channel_id", val ? String(val) : "");
                  resetPagination();
                }}
                containerStyles={{ width: "320px", zIndex: 1000 }}
                isSearchable
              />
            </Flex>
          )}
        </BaseTableFilters>

        {!isFlatListMode && !isLoading && (
          <TabsBar
            activeTab={activeTab}
            tabCounts={{ unsorted: applications.count_unsorted, assigned: applications.count_appointed }}
            onChange={(tab) => {
              setActiveTab(tab);
              resetPagination();
            }}
          />
        )}

        <BaseTable
          list={applications.items}
          columns={columns}
          clickHandler={(id) => {
            navigate(`${id}`, { state: { fromApplicationsList: true, fromList: true } });
          }}
          sortHandler={({ id, direction }) => {
            handleSort({ id, direction: direction ?? null });
            formik.setValues({
              ...formik.values,
              sort_column: `application.${id}`,
              sort_direction: direction as string,
            });
            resetPagination();
          }}
          sortState={{
            column: sortColumn || "",
            direction: sortDirection,
          }}
          isLoading={isLoading}
        />

        <AssignModal
          isOpen={isAssignOpen}
          onClose={() => setAssignOpen(false)}
          onConfirm={refreshApplications}
          applicationId={selectedApplicationId ?? undefined}
          applicationName={applications.items.find((a) => a.id === selectedApplicationId)?.name}
          customerName={applications.items.find((a) => a.id === selectedApplicationId)?.customer?.name}
          channels={channels}
          assigned={(applications.items.find((a) => a.id === selectedApplicationId)?.channels || []) as any}
        />

        <Pagination
          isLoading={isLoading}
          totalItems={applications.count}
          onPageChange={(offset, limit) => {
            const params = new URLSearchParams(searchParams);
            params.set("page", String(offset / limit + 1));
            params.set("limit", String(limit));
            navigate(`?${params.toString()}`);
            formik.setValues({
              ...formik.values,
              offset: String(offset),
              limit: String(limit),
            });
          }}
        />
      </Flex>
    </Layout>
  );
};
